import { observer } from "mobx-react-lite";
import React, { useContext } from "react";

import { Stack } from "@bps/fluent-ui";
import {
  ClinicalNoteFormat,
  ClinicalNotesSubHeadings
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { EncounterFormContext } from "../clinical-form/models/EncounterFormContext.ts";
import { FreeTextNote } from "./FreeTextNote/FreeTextNote.tsx";
import { TodaysNoteHeading } from "./TodaysNoteHeading.tsx";
import { useTodaysNotesDebounce } from "./useTodaysNotesDebounce.ts";

export const TodaysNotes: React.FunctionComponent = observer(() => {
  const { clinicalRecord, isRecordUpdate } = useContext(EncounterFormContext);

  const tab = clinicalRecord.patientRecordTab;
  const { userExperience } = useStores();
  const notesFormat = userExperience.settings.clinicalNotesFormat;

  const { updateMainFreeText, updateNotesHeading } = useTodaysNotesDebounce();

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { paddingRight: 16 } }}>
      {(isRecordUpdate ||
        (notesFormat !== ClinicalNoteFormat.SOTAP &&
          notesFormat !== ClinicalNoteFormat.SOAP)) && (
        <FreeTextNote
          heading="Consultation"
          boldHeading
          defaultValue={clinicalRecord?.mainFreeText ?? ""}
          onChange={value => {
            updateMainFreeText(value);
          }}
        />
      )}
      <DataFetcher
        fetch={async () => {
          await clinicalRecord.loadChangeConditionComment();
        }}
      >
        {() => <Stack>{clinicalRecord.changeConditionComment}</Stack>}
      </DataFetcher>
      {clinicalRecord.notesHeadings.map(heading => {
        const perDMRUserSetting = tab?.getTodaysNotesUserSettings(
          heading,
          ClinicalNotesSubHeadings.DMRImages
        );

        const perCNSUserSetting = tab?.getTodaysNotesUserSettings(
          heading,
          ClinicalNotesSubHeadings.CNSImages
        );

        const perUserSettings = tab?.getTodaysNotesUserSettings(heading);
        const showImages = perUserSettings ? perUserSettings.showImage : true;
        const showDMRImages = perDMRUserSetting
          ? perDMRUserSetting.showImage
          : true;

        const showCNSImages = perCNSUserSetting
          ? perCNSUserSetting.showImage
          : true;

        return (
          <TodaysNoteHeading
            key={heading.code}
            heading={heading}
            onChange={async value => {
              updateNotesHeading(heading, value);
            }}
            showImages={showImages}
            showDMRImages={showDMRImages}
            showCNSImages={showCNSImages}
            setShowImages={(val: boolean, key: string) => {
              tab?.setTodaysNotesUserSettings(key, { showImage: val });
            }}
          />
        );
      })}
    </Stack>
  );
});
